/**
 * Legal-document acceptance: which versioned documents (Terms of Service, Privacy Policy, the
 * Business Associate Agreement) exist, and whether the caller — or the caller's practice — has
 * accepted the current version of each. `BACKLOG.md` O3/O4, `docs/roadmap.md`'s reuse-inventory
 * table: "The versioned-document-acceptance mechanism is what BAA acceptance should reuse."
 *
 * There is no `legal/` precedent in this repo to port from (verified: no such directory exists as of
 * this change). This module is designed fresh, following the conventions every other `shared/`
 * module here already uses — Zod-as-single-source-of-truth, strict objects, no server-owned value
 * accepted from a request body — and it is deliberately pure and framework-free, matching
 * `authorization.ts`'s and `careTeam.ts`'s own split: the *decision* (is the current version
 * accepted? is this document type an org-level relationship or an individual one?) lives here, covered
 * by tests with no database, and the DB-touching half stays thin in `server/src/legal/service.ts`.
 *
 * **Why `baa` is org-scoped and `terms_of_service`/`privacy_policy` are not, stated once rather than
 * left implicit at every call site:** a BAA is a legal relationship between the *practice* and the
 * platform — the practice is the covered entity's business associate counterparty, not the individual
 * who happened to click "I accept". Terms of Service and the Privacy Policy, by contrast, are each
 * individual user's own agreement to use the product — a user who belongs to three practices has one
 * ToS acceptance, not three. Collapsing the two into "acceptance always belongs to whichever org the
 * session happened to
 * be scoped to at click time" would be wrong for ToS (a user's agreement does not multiply per
 * practice) and *dangerously* wrong for a BAA (an individual's personal acceptance is not the
 * practice's legal signature) — `ORG_SCOPED_LEGAL_DOCUMENT_TYPES` is the one place this is decided,
 * so a third document type added later has to make the same choice explicitly rather than inheriting
 * one by accident.
 */

import { z } from 'zod';

import { CAPABILITIES } from './authorization.ts';

// --- Document types ----------------------------------------------------------------------------------

/**
 * The legal-document types this build knows about. A closed union rather than open-but-checked text
 * (unlike `orgs.tier` or `vitals.vitalType`): a new legal document type is a legal/product decision
 * that should fail typecheck until every place that has to reason about it — most of all
 * `ORG_SCOPED_LEGAL_DOCUMENT_TYPES` below — has explicitly considered it, the same reasoning
 * `authorization.ts` gives for `ROLES` being closed rather than open.
 */
/**
 * `notice_of_privacy_practices` and `consent_to_treat` (`BACKLOG.md` C7, `emr-po` A16) landed
 * 2026-08-24 alongside the kiosk check-in surface. Both are **patient-scoped**, a third category
 * distinct from the org/platform split this file's header argues for `baa` vs. `terms_of_service`/
 * `privacy_policy`: a Notice of Privacy Practices acknowledgment and a consent-to-treat are each
 * *this specific patient's* agreement, not the org's and not the individual staff member who happens
 * to be signed in. See `PATIENT_SCOPED_LEGAL_DOCUMENT_TYPES` below for the acceptance-scope
 * consequence, and `server/src/kiosk/routes.ts` for the one place either type is actually accepted
 * today — a patient's own kiosk check-in session, never a staff session.
 */
export const LEGAL_DOCUMENT_TYPES = [
  'terms_of_service',
  'privacy_policy',
  'baa',
  'notice_of_privacy_practices',
  'consent_to_treat',
] as const;
export type LegalDocumentType = (typeof LEGAL_DOCUMENT_TYPES)[number];
export const legalDocumentTypeSchema = z.enum(LEGAL_DOCUMENT_TYPES);

/**
 * Which document types are an org-level legal relationship rather than an individual user's own
 * agreement. See this file's header comment for the full argument. `baa` is the only member today;
 * a future document type (a Data Processing Addendum, say) would be added to this list deliberately
 * rather than defaulting one way or the other.
 */
export const ORG_SCOPED_LEGAL_DOCUMENT_TYPES: readonly LegalDocumentType[] = ['baa'];

export function isOrgScopedLegalDocumentType(type: LegalDocumentType): boolean {
  return ORG_SCOPED_LEGAL_DOCUMENT_TYPES.includes(type);
}

/**
 * Which document types belong to one specific patient rather than to the org or to an individual
 * staff user — the kiosk check-in consent surface (`notice_of_privacy_practices`, `consent_to_treat`)
 * above. Checked ahead of `isOrgScopedLegalDocumentType` in `legalDocumentScopeFor` below, since a
 * type cannot be both.
 */
export const PATIENT_SCOPED_LEGAL_DOCUMENT_TYPES: readonly LegalDocumentType[] = [
  'notice_of_privacy_practices',
  'consent_to_treat',
];

export function isPatientScopedLegalDocumentType(type: LegalDocumentType): boolean {
  return PATIENT_SCOPED_LEGAL_DOCUMENT_TYPES.includes(type);
}

/** Where an acceptance's scope resolves to, mirroring `isOrgScopedLegalDocumentType`'s decision. */
export type LegalDocumentScope = 'platform' | 'org' | 'patient';

export function legalDocumentScopeFor(type: LegalDocumentType): LegalDocumentScope {
  if (isPatientScopedLegalDocumentType(type)) return 'patient';
  return isOrgScopedLegalDocumentType(type) ? 'org' : 'platform';
}

// --- Pure decision logic ------------------------------------------------------------------------------

/** The published-version facts `selectCurrentDocument` needs about one row. Structural, not the row. */
export interface PublishedDocumentVersion {
  readonly version: string;
  /** ISO calendar date (`YYYY-MM-DD`), matching `db/schema/columns.ts`'s `calendarDate` storage. */
  readonly effectiveDate: string;
}

/**
 * Which of several published versions of the same document type is "current" as of `asOf` —
 * the one with the latest `effectiveDate` that has actually arrived.
 *
 * Deliberately **not** a stored `isCurrent` flag on the row: `claude-kit/rules/invariants.md`'s
 * general lesson (sharpened here from `T11`'s own instance, "resolve entitlement at read time, never
 * from a stored flag") applies just as much to "which version is in force" as it does to plan
 * entitlement: a flag column drifts the moment a new
 * version is published and nothing remembers to flip the old one off, and a future-dated version
 * (published today, effective next month) must not become "current" the instant its row exists.
 *
 * A tie on `effectiveDate` (two versions published effective the same day, a data-entry mistake or a
 * same-day correction) resolves to the *last* one in `versions` — the caller is expected to pass rows
 * ordered by insertion, so a corrected same-day republish wins over the one it corrects.
 */
export function selectCurrentDocument<T extends PublishedDocumentVersion>(
  versions: readonly T[],
  asOf: Date,
): T | null {
  const asOfIso = asOf.toISOString().slice(0, 10);
  let current: T | null = null;
  for (const candidate of versions) {
    if (candidate.effectiveDate > asOfIso) continue;
    if (current === null || candidate.effectiveDate >= current.effectiveDate) {
      current = candidate;
    }
  }
  return current;
}

/** One document's identity, for `resolveAcceptanceStatus` below. */
export interface CurrentDocumentSummary {
  readonly type: LegalDocumentType;
  readonly currentVersion: string;
}

/** One acceptance record's identity, for `resolveAcceptanceStatus` below. */
export interface AcceptanceRecordSummary {
  readonly documentType: LegalDocumentType;
  readonly version: string;
}

/** `documents`, each annotated with whether an acceptance for its *current* version exists. */
export type DocumentAcceptanceStatus = CurrentDocumentSummary & { readonly accepted: boolean };

/**
 * For each document, is there an acceptance record naming its exact current version?
 *
 * Pure and DB-free by design, so the actual rule — "accepted" means the caller (or the caller's org,
 * per scope) accepted *this* version, not merely *a* version — is covered by
 * `legalDocuments.test.ts` with no database, matching `careTeam.ts`'s own split. An acceptance of a
 * superseded version does not count: `terms_of_service` v1's acceptance says nothing about v2, which
 * is the entire reason versioning exists here rather than a single boolean "has this user ever
 * agreed to our terms".
 */
export function resolveAcceptanceStatus(
  documents: readonly CurrentDocumentSummary[],
  acceptances: readonly AcceptanceRecordSummary[],
): readonly DocumentAcceptanceStatus[] {
  const acceptedTypes = new Set(
    acceptances
      .filter((acceptance) =>
        documents.some(
          (document) =>
            document.type === acceptance.documentType &&
            document.currentVersion === acceptance.version,
        ),
      )
      .map((acceptance) => acceptance.documentType),
  );
  return documents.map((document) => ({ ...document, accepted: acceptedTypes.has(document.type) }));
}

// --- Wire schemas --------------------------------------------------------------------------------------

export const MAX_LEGAL_VERSION_LENGTH = 100;
export const MAX_LEGAL_CONTENT_URL_LENGTH = 2048;
/** sha256, hex-encoded: exactly 64 hex characters. */
export const LEGAL_CONTENT_HASH_LENGTH = 64;

export const legalContentHashSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, `Expected a lowercase hex sha256 (${LEGAL_CONTENT_HASH_LENGTH} chars)`);

/**
 * One document, at its current version, from the caller's point of view: whether it applies to the
 * caller individually or to their practice, and whether that scope has accepted it.
 */
export const legalDocumentStatusSchema = z.strictObject({
  type: legalDocumentTypeSchema,
  currentVersion: z.string().max(MAX_LEGAL_VERSION_LENGTH),
  /** DB-echoed calendar date — `z.string()`, matching `clinicalRequests.ts`'s `patientResponseSchema
   * .dateOfBirth` convention of widening a DB-read date to plain text rather than reusing the
   * stricter `calendarDateSchema` request-side validator. */
  effectiveDate: z.string(),
  /**
   * Where the canonical text lives. Nullable because a document row can exist before its hosted
   * text does (see `db/schema/legal.ts`'s header comment on why this is a pointer, not stored body
   * text) — the wire shape has to represent "not yet linked" rather than force an empty string.
   */
  contentUrl: z.string().max(MAX_LEGAL_CONTENT_URL_LENGTH).nullable(),
  contentHash: legalContentHashSchema.nullable(),
  scope: z.enum(['platform', 'org', 'patient']),
  accepted: z.boolean(),
  /**
   * When the scope's acceptance of *this* version happened, or `null` if `accepted` is `false`.
   * `z.string()` rather than `z.iso.datetime()`, matching every other DB-echoed timestamp in
   * `clinicalRequests.ts` (`createdAt`/`updatedAt` throughout): this value is read back from
   * Postgres, not supplied by a caller, so it does not need — and must not risk a runtime throw
   * against — a strict ISO-format check the way a client-supplied instant does.
   */
  acceptedAt: z.string().nullable(),
});
export type LegalDocumentStatus = z.infer<typeof legalDocumentStatusSchema>;

export const legalDocumentsResponseSchema = z.strictObject({
  documents: z.array(legalDocumentStatusSchema),
});
export type LegalDocumentsResponse = z.infer<typeof legalDocumentsResponseSchema>;

/** `POST /api/legal-documents/:type/accept` has no body — the type comes from the path, the version
 * is always "whichever is current right now", never client-supplied (`claude-kit/rules/invariants.md`,
 * "Server-authoritative values never come from the request"). Kept as an explicit empty strict object
 * rather than skipping validation, so a caller sending an unexpected body is rejected rather than
 * silently ignored. */
export const acceptLegalDocumentRequestSchema = z.strictObject({});
export type AcceptLegalDocumentRequest = z.infer<typeof acceptLegalDocumentRequestSchema>;

export const legalDocumentAcceptanceSchema = z.strictObject({
  type: legalDocumentTypeSchema,
  version: z.string().max(MAX_LEGAL_VERSION_LENGTH),
  scope: z.enum(['platform', 'org', 'patient']),
  /** DB-echoed — see `legalDocumentStatusSchema.acceptedAt`'s comment for why this is `z.string()`. */
  acceptedAt: z.string(),
});
export type LegalDocumentAcceptance = z.infer<typeof legalDocumentAcceptanceSchema>;

export const acceptLegalDocumentResponseSchema = z.strictObject({
  acceptance: legalDocumentAcceptanceSchema,
});
export type AcceptLegalDocumentResponse = z.infer<typeof acceptLegalDocumentResponseSchema>;

export const LEGAL_ERROR_CODES = [
  /** The path segment was not a recognised document type. */
  'invalid_request',
  /** The document type is recognised, but no version has an `effectiveDate` on or before today —
   * a document row was created but not yet published. */
  'not_found',
  /** The caller's role does not hold the capability this route requires. */
  'forbidden',
  /** The audit trail could not be written, so the acceptance did not happen — see `routeSupport.ts`. */
  'audit_unavailable',
] as const;
export type LegalErrorCode = (typeof LEGAL_ERROR_CODES)[number];

/**
 * The non-`forbidden` failures: a bare `{ error }`, nothing else.
 *
 * Split out from `forbidden` below rather than one flat `z.strictObject({ error, capability })` with
 * `capability` made optional: an optional field would let a `not_found`/`invalid_request`/
 * `audit_unavailable` response carry a stray `capability` and still validate, which defeats the
 * point of a strict schema. A discriminated union is the shape that actually matches the wire.
 */
const legalGenericErrorResponseSchema = z.strictObject({
  error: z.enum(['invalid_request', 'not_found', 'audit_unavailable']),
});

/**
 * `forbidden` alone, carrying `capability` — matching the real shape `requireCapability` sends
 * (`auth/middleware.ts`'s `c.json({ error: 'forbidden', capability }, 403)`), not the bare
 * `{ error }` every other failure code uses. An earlier version of this schema was a flat
 * `z.strictObject({ error: z.enum(LEGAL_ERROR_CODES) })` covering all four codes uniformly, which
 * meant validating the *real* 403 response against the exported schema would throw on the extra
 * field — caught by review before merge, not by a test that actually hit the route.
 */
const legalForbiddenErrorResponseSchema = z.strictObject({
  error: z.literal('forbidden'),
  capability: z.enum(CAPABILITIES),
});

export const legalErrorResponseSchema = z.discriminatedUnion('error', [
  legalForbiddenErrorResponseSchema,
  legalGenericErrorResponseSchema,
]);
export type LegalErrorResponse = z.infer<typeof legalErrorResponseSchema>;

// --- Subprocessors (O4) --------------------------------------------------------------------------------

/**
 * A subprocessor tracking artifact — inventory, not workflow, per `BACKLOG.md` O4 ("nothing to
 * port"). This is deliberately small: a name, what it's used for, and whether a BAA (or equivalent
 * data-processing agreement) is in place with it and since when. No status workflow, no approval
 * chain, no route in this pass — see the server-side report for why an API surface was not built for
 * this yet.
 */
export const SUBPROCESSOR_BAA_STATUSES = ['not_required', 'pending', 'signed'] as const;
export type SubprocessorBaaStatus = (typeof SUBPROCESSOR_BAA_STATUSES)[number];
export const subprocessorBaaStatusSchema = z.enum(SUBPROCESSOR_BAA_STATUSES);
